import Breadcrumb from "components/Common/Breadcrumb"
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
  Spinner,
} from "reactstrap"
import { FaEye, FaFileImage, FaSave, FaSearch } from "react-icons/fa"
import { MdCancel } from "react-icons/md"
import moment from "moment"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const allowOnlyNumbers = value => value.replace(/[^0-9]/g, "")

const Expense = () => {
  document.title = "Employee Expenses | Vintage Infra Equipment Sol PVT LTD"
  const [employees, setEmployees] = useState([])
  const [employee, setEmployee] = useState("")
  const [month, setMonth] = useState(moment().format("MMMM"))
  const [year, setYear] = useState(moment().format("YYYY"))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [allExpenses, setAllExpenses] = useState([])
  const [expenseModal, setExpenseModal] = useState(false)

  // ── Documents modal state ──────────────────────────────────
  const [docModal, setDocModal] = useState(false)
  const [docLoading, setDocLoading] = useState(false)
  const [docImages, setDocImages] = useState([]) // single state for all docs
  const [previewImg, setPreviewImg] = useState(null)

  const ITEMS_PER_PAGE = 50
  const [currentPage, setCurrentPage] = useState(1)
  const [expenseDetails, setExpenseDetails] = useState([])
  const [filteredDetails, setFilteredDetails] = useState([])

  const startYear = 2020
  const currentYear = new Date().getFullYear()

  useEffect(() => {
    fetchEmployees()
    fetchExpenses()
  }, [])

  const fetchEmployees = async () => {
    const res = await fetchData(setLoading, "users")
    if (res?.success) setEmployees(res.data)
  }

  const fetchExpenses = async () => {
    const res = await fetchData(setLoading, "expense")
    if (res?.success) {
      setExpenseDetails(res.data)
      setFilteredDetails(res.data)
    } else {
      Swal.fire("Error", "Failed to load expenses", "error")
    }
  }

  useEffect(() => {
    handleFilter()
  }, [employee, month, year, expenseDetails])

  const handleFilter = () => {
    let filtered = [...expenseDetails]

    if (employee)
      filtered = filtered.filter(
        item => String(item.userId) === String(employee)
      )
    if (month)
      filtered = filtered.filter(
        item => moment(item.createdAt).format("MMMM") === month
      )
    if (year)
      filtered = filtered.filter(
        item => moment(item.createdAt).format("YYYY") === year
      )
    setFilteredDetails(filtered)
    setCurrentPage(1)
  }

  // ── Fetch documents — single array, grouped by type in UI ──
  const handleViewDocuments = async row => {
    setDocModal(true)
    setDocLoading(true)
    setDocImages([])

    try {
      const res = await FetchPostData(setDocLoading, `expense/files`, {
        date: row?.date,
        userId: row?.userId,
      })

      if (res?.data?.success) {
        // Map raw items → { type, url, name } objects
        const images = (res.data?.data || [])
          .map((item, i) => ({
            type: item.type, // "service_report_images" | "others_images"
            url: item.service_report_image // pick whichever field has data
              ? `data:image/jpeg;base64,${item.service_report_image}`
              : item.other_image
              ? `data:image/jpeg;base64,${item.other_image}`
              : null,
            name: item.fileName || `Document ${i + 1}`,
            id: item.id,
          }))
          .filter(img => img.url) // drop items with no image

        setDocImages(images)
      } else {
        Swal.fire("Error", "Failed to load documents", "error")
        setDocModal(false)
      }
    } catch (e) {
      Swal.fire("Error", "Something went wrong", "error")
      setDocModal(false)
    } finally {
      setDocLoading(false)
    }
  }

  const groupedExpenses = useMemo(() => {
    const map = {}
    filteredDetails.forEach(item => {
      if (!item.userId || !item.value || !item.type) return
      const date = moment(item.createdAt).format("DD-MM-YYYY")
      const userId = item.userId
      const key = `${date}_${userId}`
      if (!map[key]) {
        map[key] = {
          date,
          userId,
          employee: `${item?.user?.name || ""}${
            item?.user?.employeeId ? ` (${item.user.employeeId})` : ""
          }`,
          Travel: 0,
          Dearness: 0,
          Other: 0,
          OtherReason: "",
          total: 0,
          allexpenses: [],
        }
      }
      const amount = Number(item.value || 0)
      const type = item.type.toLowerCase()
      map[key].allexpenses.push({
        id: item.id,
        type: item.type,
        amount,
        description: item.description,
        createdAt: item.createdAt,
      })
      if (type === "travel") map[key].Travel += amount
      else if (type === "dearness") map[key].Dearness += amount
      else {
        map[key].Other += amount
        if (item.description)
          map[key].OtherReason += map[key].OtherReason
            ? `, ${item.description}`
            : item.description
      }
      map[key].total += amount
    })
    return Object.values(map).sort((a, b) => {
      return moment(b.date, "DD-MM-YYYY") - moment(a.date, "DD-MM-YYYY")
    })
  }, [filteredDetails])

  const totalPages = Math.ceil(groupedExpenses.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const paginatedData = groupedExpenses.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  )

  const grandTotal = groupedExpenses.reduce((sum, row) => sum + row.total, 0)
  const grandTA = groupedExpenses.reduce((sum, row) => sum + row.Travel, 0)
  const grandDA = groupedExpenses.reduce((sum, row) => sum + row.Dearness, 0)
  const grandOther = groupedExpenses.reduce((sum, row) => sum + row.Other, 0)

  const handleSaveExpenses = async item => {
    const APIdata = await FetchPutData(setLoading, "expense", item?.id, {
      value: item?.amount,
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        icon: "success",
        title: "Expense Updated Successfully.",
        text: APIdata?.data?.message,
      })
      fetchExpenses()
    } else {
      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text: APIdata?.response?.data?.message,
      })
    }
  }

  const { checkAccess } = usePermissionData()

  // ── Image grid — auto-groups by img.type ──────────────────
  // Friendly label for type keys
  const formatTypeName = type => {
    const map = {
      service_report_images: "Service Report Images",
      others_images: "Other Images",
    }
    return map[type] || type.replace(/_/g, " ")
  }

  const renderImageGrid = () => {
    if (!docImages.length)
      return (
        <div className="text-center py-5 text-muted">
          <FaFileImage size={40} className="mb-2 opacity-50" />
          <p>No documents found</p>
        </div>
      )

    // Group by type field
    const grouped = docImages.reduce((acc, img) => {
      const type = img?.type || "Other"
      if (!acc[type]) acc[type] = []
      acc[type].push(img)
      return acc
    }, {})

    return (
      <div>
        {Object.entries(grouped).map(([type, images]) => (
          <div key={type} className="mb-4">
            {/* Group heading */}
            <div className="d-flex align-items-center gap-2 mb-3">
              <h6 className="mb-0 fw-bold">{formatTypeName(type)}</h6>
              <span className="badge bg-secondary">{images.length}</span>
            </div>

            {/* Image grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: "12px",
              }}
            >
              {images.map((img, i) => (
                <div
                  key={img.id || i}
                  onClick={() => setPreviewImg(img)}
                  style={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.transform = "scale(1.03)")
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  <img
                    src={img.url}
                    alt={img.name}
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={e => {
                      e.target.src =
                        "https://via.placeholder.com/140x120?text=No+Image"
                    }}
                  />
                  <div
                    style={{
                      padding: "6px 8px",
                      fontSize: "11px",
                      color: "#555",
                      background: "#fafafa",
                    }}
                  >
                    {img.name}
                  </div>
                </div>
              ))}
            </div>

            <hr className="mt-3" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <PermittedComponent isAccess={checkAccess(12, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Payroll" breadcrumbItem="Employee Expenses" />

        <Card className="mb-3">
          <CardBody>
            {/* ── Filters ── */}
            <Row className="align-items-center">
              <Col md={2} className="position-relative">
                <Input
                  type="select"
                  className="form select rounded-4"
                  value={employee}
                  onChange={e => setEmployee(e.target.value)}
                >
                  <option value="">All Employees</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={2} className="position-relative">
                <Input
                  type="select"
                  className="form select rounded-4"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                >
                  <option value="">All Months</option>
                  {moment.months().map(m => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={2} className="position-relative">
                <Input
                  type="select"
                  className="form select rounded-4"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                >
                  <option value="">All Years</option>
                  {Array.from(
                    { length: currentYear - startYear + 1 },
                    (_, i) => startYear + i
                  )
                    .reverse()
                    .map(y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                </Input>
              </Col>
            </Row>

            {/* ── Table ── */}
            <div
              style={{
                maxHeight: "60vh",
                overflowY: "auto",
                marginTop: "1.5rem",
              }}
            >
              <Table striped className="mb-0" style={{ tableLayout: "fixed" }}>
                <thead
                  className="table-dark"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <td style={{ width: "3%" }}>#</td>
                    <td style={{ width: "10%" }}>Date</td>
                    <td style={{ width: "20%" }}>Employee</td>
                    <td>Travel</td>
                    <td>Dearness</td>
                    <td>Other</td>
                    <td>Total</td>
                    <td>View</td>
                    <td>Documents</td>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}
                  {paginatedData.map((row, index) => (
                    <tr key={index}>
                      <td>{startIndex + index + 1}</td>
                      <td>{row.date}</td>
                      <td>{row.employee}</td>
                      <td>₹ {row.Travel}</td>
                      <td>₹ {row.Dearness}</td>
                      <td>₹ {row.Other}</td>
                      <td className="fw-bold">₹ {row.total}</td>
                      <td>
                        <Button
                          onClick={() => {
                            setAllExpenses(row?.allexpenses)
                            setExpenseModal(true)
                          }}
                        >
                          <FaEye />
                        </Button>
                      </td>
                      <td>
                        <Button
                          className="btn-blue"
                          onClick={() => handleViewDocuments(row)}
                        >
                          <FaFileImage />
                        </Button>
                      </td>
                    </tr>
                  ))}

                  {/* Grand Total Row */}
                  {groupedExpenses.length > 0 && (
                    <tr className="table-secondary fw-bold">
                      <td colSpan={3} className="text-end pe-4">
                        Grand Total
                      </td>
                      <td>₹ {grandTA}</td>
                      <td>₹ {grandDA}</td>
                      <td>₹ {grandOther}</td>
                      <td>₹ {grandTotal}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <nav className="d-flex justify-content-between mt-3">
                <h6>
                  Showing {groupedExpenses.length === 0 ? 0 : startIndex + 1} to{" "}
                  {Math.min(
                    startIndex + ITEMS_PER_PAGE,
                    groupedExpenses.length
                  )}{" "}
                  of {groupedExpenses.length} entries
                </h6>
                <ul className="pagination mb-0">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li
                      key={i + 1}
                      className={`page-item ${
                        currentPage === i + 1 ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage(p => Math.min(p + 1, totalPages))
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </CardBody>
        </Card>

        {/* ══════════════ Expense Detail Modal ══════════════ */}
        <Modal isOpen={expenseModal} size="lg" centered>
          <ModalHeader toggle={() => setExpenseModal(false)}>
            Expense Details
          </ModalHeader>
          <ModalBody>
            <Table>
              <thead className="table-dark">
                <tr>
                  <td>#</td>
                  <td>Type</td>
                  <td>Description</td>
                  <td>Amount</td>
                  <td>Action</td>
                </tr>
              </thead>
              <tbody>
                {allExpenses.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td className="text-capitalize">{item.type}</td>
                    <td
                      style={{
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {item.description || "--"}
                    </td>
                    <td>
                      <Input
                        type="text"
                        className="form-control rounded-4 w-auto"
                        value={item.amount}
                        onChange={e => {
                          const value = allowOnlyNumbers(e.target.value)
                          setAllExpenses(prev =>
                            prev.map(exp =>
                              exp.id === item.id
                                ? { ...exp, amount: value }
                                : exp
                            )
                          )
                        }}
                      />
                    </td>
                    <td>
                      {checkAccess(12, "edit") && (
                        <Button
                          className="btn btn-blue"
                          disabled={saving}
                          onClick={() => handleSaveExpenses(item)}
                        >
                          <FaSave />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>

        {/* ══════════════ Documents Modal ══════════════ */}
        <Modal
          isOpen={docModal}
          size="xl"
          toggle={() => setDocModal(false)}
          centered
        >
          <ModalHeader toggle={() => setDocModal(false)}>
            <FaFileImage className="me-2" /> Expense Documents
            {!docLoading && (
              <span className="badge bg-secondary ms-2 fw-normal fs-6">
                {docImages.length} files
              </span>
            )}
          </ModalHeader>
          <ModalBody>
            {docLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading documents...</p>
              </div>
            ) : (
              renderImageGrid()
            )}
          </ModalBody>
        </Modal>

        {/* ══════════════ Full Image Preview Modal ══════════════ */}
        <Modal
          isOpen={!!previewImg}
          size="lg"
          toggle={() => setPreviewImg(null)}
          centered
        >
          <ModalHeader toggle={() => setPreviewImg(null)}>
            {previewImg?.name || "Preview"}
          </ModalHeader>
          <ModalBody className="text-center p-2">
            <img
              src={previewImg?.url || previewImg}
              alt="preview"
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                borderRadius: "8px",
              }}
            />
          </ModalBody>
        </Modal>
      </div>
    </PermittedComponent>
  )
}

const clearIconStyle = {
  position: "absolute",
  right: "20%",
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
}

export default Expense
